import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceRowType } from '../../../../interfaces/myData.interface';

interface Props {
  products?: InvoiceRowType | InvoiceRowType[];
}

const ClassificationRows = ({
  label,
  data,
}: {
  label: string;
  data?: Record<string, any> | Record<string, any>[];
}) => {
  const { t } = useTranslation();
  if (!data) return null;
  const rows = Array.isArray(data) ? data : [data];
  return (
    <div className="margin-t-4 padding-l-12">
      <div className="boldText4">{label}:</div>
      {rows.map((c, i) => {
        const type = c['icls:classificationType'] ?? c['ecls:classificationType'];
        const category = c['icls:classificationCategory'] ?? c['ecls:classificationCategory'];
        const amount = c['icls:amount'] ?? c['ecls:amount'];
        return (
          <div key={i} className="normalText4 padding-l-10">
            {[type, category].filter(Boolean).join(' / ')}
            {amount != null ? ` · ${amount}` : ''}
          </div>
        );
      })}
    </div>
  );
};

export const GsisDocProductsList = ({ products }: Props) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!products) return [];
    return Array.isArray(products) ? products : [products];
  }, [products]);

  if (!rows || rows.length === 0) return null;

  return (
    <div className="simpleBorder p-12 width-100">
      <div className="boldText5 margin-b-8">{t('DELIVERY.PRODUCTS') || 'Products'}</div>

      {rows.map((r, idx) => (
        <div key={idx} className="margin-b-12" style={{ borderBottom: '1px solid var(--ion-border-color, #333)', paddingBottom: 8 }}>
          {/* Line header */}
          <div className="flex-start-center-row-nowrap gap-8 margin-b-4">
            <div className="boldText4" style={{ minWidth: 24 }}>{r?.lineNumber ?? idx + 1}.</div>
            <div className="mediumText4" style={{ flex: 1 }}>{r?.itemDescr || '-'}</div>
          </div>

          {/* Core values row */}
          <div className="flex-start-start-row-wrap gap-12 padding-l-30">
            {r?.itemCode && (
              <div className="flex-start-center-row-nowrap">
                <div className="boldText4 padding-r-4">{t('DELIVERY.ITEM_CODE')}:</div>
                <div className="normalText4">{r.itemCode}</div>
              </div>
            )}
            {r?.quantity != null && (
              <div className="flex-start-center-row-nowrap">
                <div className="boldText4 padding-r-4">{t('DELIVERY.QUANTITY')}:</div>
                <div className="normalText4">{r.quantity}</div>
                {r?.measurementUnit != null && (
                  <div className="normalText4 padding-l-4">
                    {t(`DELIVERY.MEASUREMENT_UNIT_${r.measurementUnit}`, { defaultValue: String(r.measurementUnit) })}
                  </div>
                )}
              </div>
            )}
            <div className="flex-start-center-row-nowrap">
              <div className="boldText4 padding-r-4">{t('DELIVERY.NET_VALUE')}:</div>
              <div className="normalText4">{r.netValue ?? '-'}</div>
            </div>
            <div className="flex-start-center-row-nowrap">
              <div className="boldText4 padding-r-4">{t('DELIVERY.VAT_CATEGORY')}:</div>
              <div className="normalText4">
                {r.vatCategory != null
                  ? t(`DELIVERY.VAT_CATEGORY_${r.vatCategory}`, { defaultValue: String(r.vatCategory) })
                  : '-'}
              </div>
            </div>
            <div className="flex-start-center-row-nowrap">
              <div className="boldText4 padding-r-4">{t('DELIVERY.VAT_AMOUNT')}:</div>
              <div className="normalText4">{r.vatAmount ?? '-'}</div>
            </div>
            {r?.discountOption != null && (
              <div className="flex-start-center-row-nowrap">
                <div className="boldText4 padding-r-4">{t('DELIVERY.DISCOUNT_OPTION')}:</div>
                <div className="normalText4">{r.discountOption ? t('DELIVERY.YES') : t('DELIVERY.NO')}</div>
              </div>
            )}
          </div>

          {/* Classifications */}
          <div className="padding-l-30">
            {r?.incomeClassification && (
              <ClassificationRows
                label={t('DELIVERY.INCOME_CLASSIFICATION')}
                data={r.incomeClassification as any}
              />
            )}
            {r?.expensesClassification && (
              <ClassificationRows
                label={t('DELIVERY.EXPENSES_CLASSIFICATION')}
                data={r.expensesClassification as any}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
